/**
 * Toast notification system.
 *
 * Wraps the app once; consumers call `useToast()` to enqueue toasts:
 *
 *   const toast = useToast();
 *   toast.success("Profile saved.");
 *   toast.error(parseApiError(err));
 *
 * Each call returns immediately; the toast auto-dismisses after a
 * severity-based duration (success/info = 4s, warning = 5s, error = 7s).
 * The snackbar stack limits itself to 3 visible toasts so older ones
 * roll off gracefully.
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";

const ToastContext = createContext(null);

const DURATIONS = {
    success: 4000,
    info: 4000,
    warning: 5000,
    error: 7000,
};

function ToastItem({ toast, onDismiss }) {
    const [paused, setPaused] = useState(false);
    const dismissedRef = useRef(false);

    useEffect(() => {
        if (paused || toast.duration <= 0) return undefined;
        const t = setTimeout(() => {
            dismissedRef.current = true;
            onDismiss(toast.id);
        }, toast.duration);
        return () => clearTimeout(t);
    }, [paused, toast.duration, toast.id, onDismiss]);

    return (
        <Box
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            sx={{ pointerEvents: "auto" }}
        >
            <Alert
                severity={toast.severity}
                variant="filled"
                onClose={() => {
                    if (dismissedRef.current) return;
                    dismissedRef.current = true;
                    onDismiss(toast.id);
                }}
                action={toast.action}
                sx={(theme) => ({
                    minWidth: 300,
                    maxWidth: 440,
                    borderRadius: "12px",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    lineHeight: 1.45,
                    padding: "10px 14px 10px 18px",
                    alignItems: "center",
                    // White text + white icon — MuiPaper's global color rule is
                    // overridden in theme/index.js for filled severity alerts.
                    color: "#fff",
                    boxShadow:
                        "0 18px 40px -12px rgba(15,23,42,0.32), 0 4px 10px -4px rgba(15,23,42,0.18)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    // Severity-tinted left accent gives a stronger identity than
                    // the flat filled background alone.
                    borderLeft: `4px solid ${
                        theme.palette[toast.severity]?.light || "#fff"
                    }`,
                    "& .MuiAlert-message": {
                        padding: "2px 0",
                        color: "inherit",
                    },
                    "& .MuiAlert-icon": {
                        color: "#fff",
                        opacity: 0.95,
                        marginRight: 1.25,
                        fontSize: "1.3rem",
                    },
                    "& .MuiAlert-action": {
                        paddingTop: 0,
                        paddingBottom: 0,
                        marginRight: 0,
                        alignItems: "center",
                    },
                    "& .MuiAlert-action .MuiIconButton-root": {
                        color: "rgba(255,255,255,0.85)",
                        padding: "6px",
                        transition: "all 140ms ease",
                        "&:hover": {
                            color: "#fff",
                            backgroundColor: "rgba(255,255,255,0.14)",
                        },
                    },
                })}
            >
                {toast.message}
            </Alert>
        </Box>
    );
}

export function ToastProvider({ children, maxVisible = 3 }) {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const enqueue = useCallback(
        (severity, message, opts = {}) => {
            const id = ++idRef.current;
            const duration =
                typeof opts.duration === "number" ? opts.duration : DURATIONS[severity];
            const toast = {
                id,
                severity,
                message,
                duration,
                action: opts.action || null,
            };
            setToasts((prev) => [...prev.slice(-(maxVisible - 1)), toast]);
            return id;
        },
        [maxVisible]
    );

    const api = useMemo(
        () => ({
            success: (msg, opts) => enqueue("success", msg, opts),
            info: (msg, opts) => enqueue("info", msg, opts),
            warning: (msg, opts) => enqueue("warning", msg, opts),
            warn: (msg, opts) => enqueue("warning", msg, opts),
            error: (msg, opts) => enqueue("error", msg, opts),
            dismiss,
        }),
        [enqueue, dismiss]
    );

    return (
        <ToastContext.Provider value={api}>
            {children}
            <Stack
                spacing={1.25}
                sx={{
                    position: "fixed",
                    right: { xs: 16, sm: 24 },
                    bottom: { xs: 16, sm: 24 },
                    zIndex: (t) => t.zIndex.snackbar + 10,
                    pointerEvents: "none",
                    maxWidth: "calc(100vw - 32px)",
                }}
            >
                {toasts.map((t) => (
                    <Box
                        key={t.id}
                        sx={{
                            pointerEvents: "none",
                            // Slide in from the right. Custom CSS keyframes
                            // avoid MUI's <Snackbar><Slide/></Snackbar>, whose
                            // `forceReflow(node)` calls `node.scrollTop` on a
                            // DOM node that is null after unmount → "Cannot
                            // read properties of null (reading 'scrollTop')".
                            animation: "pt-toast-in 240ms cubic-bezier(0.16, 1, 0.3, 1) both",
                            "@keyframes pt-toast-in": {
                                from: { opacity: 0, transform: "translateX(24px)" },
                                to:   { opacity: 1, transform: "translateX(0)" },
                            },
                            "@media (prefers-reduced-motion: reduce)": {
                                animation: "none",
                            },
                        }}
                    >
                        <ToastItem toast={t} onDismiss={dismiss} />
                    </Box>
                ))}
            </Stack>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // Graceful no-op so component-render-time exceptions in dev don't block the tree.
        if (typeof console !== "undefined") {
            // eslint-disable-next-line no-console
            console.warn(
                "[ToastProvider] useToast() called outside provider — falling back to no-op."
            );
        }
        return {
            success: () => {},
            info: () => {},
            warning: () => {},
            warn: () => {},
            error: () => {},
            dismiss: () => {},
        };
    }
    return ctx;
}

export default ToastProvider;
